import { DossierMetaData, DossierType } from "../models/dossier-metadata";
import { SearchResult } from "../models/search-result";
import { StringSanitizer } from "../utils/StringSanitizer";
import { BaseClient } from "./baseClient";
import { mockSearchResult } from "./mocks/document-metadata.mock";

const stringSanitizer = new StringSanitizer();

export interface IDossierMetadataApi {
  createMetadataSession(meta: DossierMetaData): Promise<{ uploadUrl: string; pid: string }>;
  updateMetadata(meta: DossierMetaData, id: string): Promise<DossierMetaData>;
  fetchMetadata(id: string): Promise<DossierMetaData>;
  getDocumentBijlages(id: string): Promise<SearchResult[]>;
}

export const mockDossier: DossierMetaData = {
  pid: "",
  titel: "",
  dossierHouder: { id: "test", label: "test" },
  type: DossierType.overig,
  identificatiekenmerken: [],
};

function safeParse<T>(jsonString: string): T {
  return JSON.parse(jsonString) as T;
}

export class MockDossierMetadataApi implements IDossierMetadataApi {
  createMetadataSession(meta: DossierMetaData): Promise<{ uploadUrl: string; pid: string; sessionId: string }> {
    if (meta.titel === "error") {
      throw new Error("Error creating metadata session");
    }
    return Promise.resolve({
      uploadUrl: "https://uploadmetadocument.com",
      pid: "123",
      sessionId: "123",
    });
  }
  updateMetadata(meta: DossierMetaData, id: string): Promise<DossierMetaData> {
    return Promise.resolve(safeParse<DossierMetaData>(JSON.stringify(mockDossier)));
  }
  fetchMetadata(id: string): Promise<DossierMetaData> {
    if (id === stringSanitizer.extractIdentifierFromPid(mockDossier.pid)) {
      return Promise.resolve(mockDossier);
    }

    return Promise.resolve(mockDossier);
  }
  getDocumentBijlages(id: string): Promise<SearchResult[]> {
    return new Promise((resolve) => {
      setTimeout(() => resolve([mockSearchResult, mockSearchResult]), 500);
    });
  }
}

export class DossierMetadataApi extends BaseClient implements IDossierMetadataApi {
  constructor() {
    super(
      new Headers({
        "Content-type": "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      }),
      (process.env.REACT_APP_WEBLOCATIE === "true" && process.env.REACT_APP_METADATA_API_ENDPOINT_V2
        ? process.env.REACT_APP_METADATA_API_ENDPOINT_V2
        : process.env.REACT_APP_METADATA_API_ENDPOINT) as string
    );
  }

  createMetadataSession = async (meta: DossierMetaData): Promise<{ uploadUrl: string; pid: string }> => {
    let body: DossierMetaData = { ...meta };

    const { json, headers } = await this.api<DossierMetaData>("dossier", undefined, "POST", body);

    if (!json) throw new Error(`Failed to create metada`);

    const pid = json.pid ?? "";
    const pidComonents = json.pid!.split("/");
    const id = pidComonents.at(-1);

    return {
      uploadUrl: `${process.env.REACT_APP_DOCUMENT_API_ENDPOINT}/documenten/${id}`,
      pid,
    };
  };

  updateMetadata = async (meta: DossierMetaData, id: string) => {
    let body: DossierMetaData = { ...meta };

    const { json } = await this.api<DossierMetaData>("dossier", id, "PUT", body);

    if (!json) throw new Error(`Failed to update metadata for ID: ${id}`);

    return json;
  };

  fetchMetadata = async (id: string) => {
    const { json } = await this.api<DossierMetaData>("dossier", id, "GET");

    if (!json) throw new Error(`Failed to fetch metadata for ID: ${id}`);

    return json;
  };

  getDocumentBijlages = async (id: string) => {
    const { json } = await this.api<SearchResult[]>("dossier", id, "GET", undefined, undefined, "/bijlagen");

    if (!json) throw new Error(`Failed to fetch metadata for ID: ${id}`);

    return json;
  };
}
